import type { Feature, FeatureCollection, Point } from 'geojson'
import type { GeoJSONSource, Map } from 'maplibre-gl'
import { clearOf, fromMercatorPx, mercatorPx, mergeCrowded, type CountedMark, type Obstacle } from './crowding'
import {
  JOURNAL_COUNTRY_DROP_PX,
  JOURNAL_NAME_DROP_PX,
  JOURNAL_RADIUS_STOPS,
  MARK_COUNT_FONT,
  MAX_ZOOM,
  TASTING_PIN_COLOR,
  TASTING_PIN_RADII,
  TASTING_PIN_RING_COLOR,
  TASTING_SOURCE_ID,
  WINERY_MARK_BADGE_OFFSET,
  WINERY_MARK_BADGE_RADIUS,
  WINERY_MARK_BADGE_WIDE_FROM,
  WINERY_MARK_BADGE_WIDE_RADIUS,
  WINERY_MARK_DOT_RADIUS,
  WINERY_MARK_MIN_ZOOM,
  WINERY_MARK_REACH_PX,
  WINERY_MARK_RING_WIDTH,
  WISHLIST_PIN_FILL_COLOR,
  WISHLIST_SOURCE_ID,
} from '../../lib/map_config'
import type { JournalLayer } from '../../types/atlas'

/**
 * R21's two layers: "my tastings" and "my wishlist", over the atlas polygons.
 *
 * What each mark looks like, and why the reader's marks are claret discs, is
 * `TASTING_PIN_COLOR` in map_config.ts. This file is where they go.
 *
 * Appended with the style, after the winery layers, and naming no slot
 * (slots.ts): the reader's own pins are the top of the stack, above every
 * appellation tier whenever it arrives and above the winery dots.
 *
 * COUNTED HERE, NOT BY MAPLIBRE. Several tastings genuinely share one point:
 * every wine from an appellation lands on that appellation's one point, and
 * every wine the atlas can place no nearer lands on its country's. So overlap
 * is the normal case, and a number in a disc is the honest mark for it — a
 * scatter of invented offsets would put pins where nothing was drunk.
 *
 * The counting used to be the source's own (`cluster: true`), which runs in
 * the worker and tells this app nothing about where a disc ended up. That is
 * what let the reader's "44" be painted squarely over the winery chips off
 * California, on the first screen a signed-in reader sees: two sets of counted
 * discs, collected in the same places because a reader tastes where the
 * wineries are, and neither knowing the other was there. Counted here
 * (`journalMarks`), a tasting disc knows where the winery chips are and steps
 * clear of them, and a disc that stands for a country's worth of unplaced
 * wines can be set under the country's name instead of on it.
 *
 * AND ONE MARK IS NOT COUNTED AT ALL, BECAUSE IT IS EXACT. The wines of a
 * producer the reader has linked to a winery are not "somewhere in the
 * valley": they are at that winery's own point, where the winery layer has
 * already drawn its dot. Where that dot is drawn, they are a WINERY MARK on
 * it (`WINERY_MARK_MIN_ZOOM` in map_config.ts has the drawing): never stepped
 * aside, never hung under an appellation's name, and opening the winery on a
 * click. Everywhere else they are a counted disc like any other.
 *
 * WHAT THAT COSTS, AND WHEN. Nothing per frame and nothing per pointer move.
 * The marks are worked out when the tastings change, when the winery chips
 * do, and when the camera comes to rest at a new zoom — once per settle, over
 * the places a reader has tasted in (a few dozen points), and the source is
 * handed them only if they differ from what it holds. A visitor has no
 * tastings and none of it runs. Between two settles the discs are the ones
 * for the zoom the camera left, exactly as the winery chips are.
 *
 * Types are imported from maplibre-gl, values never — this module is reached
 * only through use_maplibre.ts, which is itself behind the layout's dynamic
 * import. The same rule region_layer.ts follows.
 */

// Re-exported so use_maplibre has one import site for everything about these
// layers, rather than reaching past this module into map_config for the ids.
export { TASTING_SOURCE_ID, WISHLIST_SOURCE_ID } from '../../lib/map_config'

export const TASTING_CLUSTER_LAYER_ID = 'journal-tastings-cluster'
export const TASTING_CLUSTER_COUNT_LAYER_ID = 'journal-tastings-cluster-count'
export const TASTING_POINT_LAYER_ID = 'journal-tastings-point'
export const TASTING_POINT_CORE_LAYER_ID = 'journal-tastings-point-core'
// The winery mark's three layers: its dot, the badge beside the dot, and the
// number in the badge. The dot and the badge are what a pointer or a click is
// tested against.
export const TASTING_WINERY_LAYER_ID = 'journal-tastings-winery'
export const TASTING_WINERY_BADGE_LAYER_ID = 'journal-tastings-winery-badge'
export const TASTING_WINERY_COUNT_LAYER_ID = 'journal-tastings-winery-count'
export const WISHLIST_POINT_LAYER_ID = 'journal-wishlist-point'

// Every layer the reader's tastings are drawn with, which is what "My
// tastings" switched off has to reach: a winery mark is their tastings too.
export function tastingLayerIds(): string[] {
  return [
    TASTING_CLUSTER_LAYER_ID,
    TASTING_CLUSTER_COUNT_LAYER_ID,
    TASTING_POINT_LAYER_ID,
    TASTING_POINT_CORE_LAYER_ID,
    TASTING_WINERY_LAYER_ID,
    TASTING_WINERY_BADGE_LAYER_ID,
    TASTING_WINERY_COUNT_LAYER_ID,
  ]
}

// ---------------------------------------------------------------------------
// Which marks, and where
// ---------------------------------------------------------------------------

/**
 * What a COUNTED mark carries. The two `point_count` names are the ones a
 * MapLibre cluster had, kept so the layers' filters and a reader of the style
 * find what they expect; the rest is this app's.
 */
export type JournalMarkProperties = {
  point_count: number
  point_count_abbreviated: string
  /** The place a click opens the journal for, or '' when the mark spans several. */
  place_slug: string
  /** The winery a click opens, when every tasting in the mark is at that one winery; else ''. */
  winery_slug: string
  /** What the pointer is told: the winery, the place, or how many tastings. */
  label: string
  /** And the line under it. */
  detail: string
  /** Every tasting in it is placed no nearer than its country. */
  country: boolean
}

/**
 * What a WINERY MARK carries. Deliberately neither of the other two: no
 * `point_count`, so the counted disc's layers leave it alone, and no `id`, so
 * it is not taken for one tasting's pin. `wine_count` is what its own three
 * layers filter on.
 */
export type WineryMarkProperties = {
  /** The winery a click opens. */
  winery_slug: string
  /** The winery's name, for the pointer. */
  label: string
  /** And the line under it: "17 of your wines". */
  detail: string
  wine_count: number
  wine_count_abbreviated: string
}

type MarkFeature = Feature<Point, Record<string, unknown>>
export type JournalMarks = FeatureCollection<Point, Record<string, unknown>>

/** How big the disc for a number of tastings is drawn, in px: what the paint works out. */
export function journalRadiusFor(count: number): number {
  const stops = JOURNAL_RADIUS_STOPS
  const root = Math.sqrt(Math.max(count, 0))
  const last = stops[stops.length - 1]
  if (root <= stops[0][0]) return stops[0][1]
  if (root >= last[0]) return last[1]

  const next = stops.findIndex(([at]) => at > root)
  const [fromAt, fromPx] = stops[next - 1]
  const [toAt, toPx] = stops[next]

  return fromPx + ((toPx - fromPx) * (root - fromAt)) / (toAt - fromAt)
}

// One place a reader has tasted in, or several the screen cannot tell apart.
// `named` is where the map has set that place's name, when it is drawing the
// place: the mark then hangs under the name instead of sitting on the point
// the server sent.
//
// `closer` is set on a mark that two WINERY MARKS were made into, where the
// winery layer draws dots, when a closer zoom would draw them apart again. A
// click on such a mark looks closer rather than opening anything, so it names
// no place, even the one its wineries share. No other merge sets it: every
// other mark is named by what its tastings have in common (`counted`), as it
// was before a winery had a mark.
interface Site extends CountedMark {
  features: JournalLayer['features']
  named: boolean
  closer?: boolean
}

/** Where the map has lettered a place, by slug, or null when it is not drawing it. */
export type NameAnchor = (slug: string) => readonly number[] | null

const nowhere: NameAnchor = () => null

const tastings = (count: number) => `${count.toLocaleString('en-US')} ${count === 1 ? 'tasting' : 'tastings'}`

// What a winery's mark says under the pointer. "1 of your wines" is right as
// it stands: the plural is the reader's wines, not the one.
const winesFromHere = (count: number) => `${count.toLocaleString('en-US')} of your wines`

// 1,200 tastings in one disc is "1.2k": the disc is 20 px across.
const abbreviated = (count: number) =>
  count < 1000 ? String(count) : `${(Math.round(count / 100) / 10).toLocaleString('en-US')}k`

const roundDegrees = (value: number) => Math.round(value * 1e5) / 1e5

function absorb(into: Site, other: Site) {
  into.features = [...into.features, ...other.features]
  // Two places in one mark stand on neither one's name.
  into.named = false
  // And a mark that comes apart closer in still does, whatever it has taken in.
  if (other.closer) into.closer = true
}

// The room a winery mark is taken to need, whatever its count: the dot and
// the badge are one size.
const wineryMarkReach = () => WINERY_MARK_REACH_PX

/**
 * The marks the tastings are drawn as at a zoom, clear of the winery chips.
 *
 * 1. Tastings on one point are one site: an appellation's wines, or a
 *    country's.
 * 2. Sites whose discs would touch at this zoom are merged (crowding.ts),
 *    largest first — the same rule the winery chips are merged by. PLACED
 *    tastings and COUNTRY-ONLY ones are merged apart from each other: they are
 *    drawn as two different marks, and one disc cannot be both. (Merged
 *    together, the 67 wines known only as American swallowed one from Paso
 *    Robles and became a solid "68" in Kansas.)
 * 3. A placed mark that would lie on a winery chip steps clear of it. And
 *    where the map is DRAWING the place a mark stands for, the mark hangs
 *    under that place's name (`anchor`) rather than on the point the server
 *    sent: that point is the middle of the place's bounding box, which for one
 *    appellation in ten is outside its line. Temecula Valley's eighteen
 *    tastings sat on the notch of its boundary, on the amber line itself.
 * 4. A country-only mark drops under its point, clear of the country's name,
 *    and then steps clear of the chips and of the placed marks.
 *
 * One tasting alone at a place the atlas can point to stays what it was: the
 * tasting's own feature, with its id, which is what a click opens. Everything
 * else is a counted mark — including ONE tasting known only by its country,
 * which is drawn as a "1" in the country disc rather than as a pin claiming a
 * spot in the middle of it.
 *
 * AND BEFORE ALL OF THAT, THE WINERIES. A tasting that says which winery it
 * stands at (`winery_slug`) is one of a WINERY SITE: every wine of that
 * winery, at the winery's own point. It is never looked up in `anchor` and
 * never hung under an appellation's name. It is exact.
 *
 * - At `wineryZoom` and closer, with no winery chip on screen, the winery
 *   layer is drawing that winery's dot, and the site is drawn as a WINERY
 *   MARK on it: at its exact point, not rounded, not stepped aside. Two marks
 *   that would touch (`WINERY_MARK_REACH_PX`) cannot both be read, so those
 *   two are made one ordinary site instead and join the placed tastings. A
 *   click on that one looks closer, where the two stand apart, unless no zoom
 *   the map has would part them (`comesApart`): then it is their place's.
 * - Otherwise there is no dot to stand on (further out the winery layer draws
 *   chips), and every winery site is an ordinary placed site. A mark is never
 *   stood on a chip: at these zooms the server answers with dots and never
 *   with chips, so chips are on screen only while the layer still draws the
 *   frame before.
 *
 * An ordinary counted disc that turns out to be ONE winery's wines says so
 * (`winery_slug`), and a click on it opens that winery.
 *
 * `chips` is the winery layer's chips as they are drawn — where each is and
 * how big — or nothing, when that layer is showing dots or is switched off.
 *
 * Pure: the same tastings, zoom, chips and names give the same marks.
 */
export function journalMarks(
  layer: JournalLayer,
  zoom: number,
  chips: readonly Obstacle[],
  anchor: NameAnchor = nowhere,
  wineryZoom: number = WINERY_MARK_MIN_ZOOM,
): JournalMarks {
  const placed = new globalThis.Map<string, Site>()
  const unplaced = new globalThis.Map<string, Site>()
  const wineries = new globalThis.Map<string, Site>()

  for (const feature of layer.features) {
    if (feature.geometry.type !== 'Point') continue

    // By winery, which is exact: its wines are on its own point whatever
    // appellation the atlas has put the winery in, and when it has put it in
    // none.
    const winery = feature.properties.winery_slug
    if (winery) {
      const site = wineries.get(winery)

      if (site) {
        site.count += 1
        site.features.push(feature)
        continue
      }

      const [lng, lat] = feature.geometry.coordinates
      wineries.set(winery, { lng, lat, count: 1, features: [feature], named: false })
      continue
    }

    const country = feature.properties.country === true
    const sites = country ? unplaced : placed
    // By place, which is what shares a point: every wine from an appellation
    // is on that appellation's one point.
    const key = `${feature.properties.place_slug}@${feature.geometry.coordinates.join(',')}`
    const site = sites.get(key)

    if (site) {
      site.count += 1
      site.features.push(feature)
      continue
    }

    const slug = feature.properties.place_slug
    const name = country || !slug ? null : anchor(slug)
    const [lng, lat] = name ?? feature.geometry.coordinates

    sites.set(key, { lng, lat, count: 1, features: [feature], named: name !== null })
  }

  const features: MarkFeature[] = []
  // What a country-only mark has to keep off: the chips, every winery mark and
  // every placed mark.
  const taken: Obstacle[] = [...chips]

  // Where the winery layer is drawing dots, a winery's wines are a mark on its
  // dot; everywhere else, and for two wineries the screen cannot tell apart,
  // they are placed sites like any other.
  const onDots = zoom >= wineryZoom && chips.length === 0
  const wineryWines = [...wineries.values()]
  const wineryMarks = onDots ? mergeCrowded(wineryWines, zoom, wineryMarkReach, 2, absorb) : wineryWines

  // Whether a closer zoom would draw the wineries in a merged mark apart. Not
  // when they share a point (two tasting rooms at one address), and not when
  // they are nearer than two marks can stand at the closest zoom the map has,
  // which is about a hundred metres. Looking closer at those ends on a disc
  // that opens nothing, drawn over both wineries' dots.
  const comesApart = (parts: readonly Site[]) =>
    zoom < MAX_ZOOM && mergeCrowded(parts, MAX_ZOOM, wineryMarkReach, 2).length > 1

  wineryMarks.forEach((site, index) => {
    if (!onDots) {
      placed.set(`winery ${index}`, site)
      return
    }

    // The winery sites this one was made of: itself, unless marks touched.
    const parts = wineryWines.filter((winery) => site.features.includes(winery.features[0]))
    if (parts.length > 1) {
      placed.set(`winery ${index}`, { ...site, closer: comesApart(parts) })
      return
    }

    const { winery_slug: slug, winery } = site.features[0].properties
    const properties: WineryMarkProperties = {
      winery_slug: slug ?? '',
      label: winery ?? '',
      detail: winesFromHere(site.count),
      wine_count: site.count,
      wine_count_abbreviated: abbreviated(site.count),
    }

    // The winery's own coordinates, as they came: the mark is on the dot.
    features.push({ type: 'Feature', properties, geometry: { type: 'Point', coordinates: [site.lng, site.lat] } })
    taken.push({ lng: site.lng, lat: site.lat, radius: WINERY_MARK_REACH_PX })
  })

  for (const site of mergeCrowded([...placed.values()], zoom, journalRadiusFor, 2, absorb)) {
    const alone = site.count === 1
    const radius = alone ? TASTING_PIN_RADII.ring : journalRadiusFor(site.count)
    let at: CountedMark = site

    // On a name the map has set: under it, by the mark's own size and a line
    // of lettering.
    if (site.named) {
      const [x, y] = mercatorPx(site.lng, site.lat, zoom)
      const [underLng, underLat] = fromMercatorPx(x, y + radius + JOURNAL_NAME_DROP_PX, zoom)
      at = { lng: underLng, lat: underLat, count: site.count }
    }

    const [lng, lat] = chips.length > 0 ? clearOf(at, radius, chips, zoom) : [at.lng, at.lat]
    const geometry: Point = { type: 'Point', coordinates: [roundDegrees(lng), roundDegrees(lat)] }

    taken.push({ lng, lat, radius })

    if (alone) {
      const { properties } = site.features[0]

      features.push({
        type: 'Feature',
        properties: { ...properties, detail: properties.producer ? `Your tasting · ${properties.producer}` : 'Your tasting' },
        geometry,
      })
      continue
    }

    features.push({ type: 'Feature', properties: counted(site, false), geometry })
  }

  for (const site of mergeCrowded([...unplaced.values()], zoom, journalRadiusFor, 2, absorb)) {
    const radius = journalRadiusFor(site.count)
    const [x, y] = mercatorPx(site.lng, site.lat, zoom)
    const [underLng, underLat] = fromMercatorPx(x, y + radius + JOURNAL_COUNTRY_DROP_PX, zoom)
    const [lng, lat] = clearOf({ lng: underLng, lat: underLat, count: site.count }, radius, taken, zoom)

    features.push({
      type: 'Feature',
      properties: counted(site, true),
      geometry: { type: 'Point', coordinates: [roundDegrees(lng), roundDegrees(lat)] },
    })
  }

  return { type: 'FeatureCollection', features }
}

// What a counted mark says: how many, where a click goes, and the two lines
// the pointer is told.
//
// A mark is named by what EVERY tasting in it has in common, and that is what
// a click opens: the one winery, or else the one place. A valley's wines are
// the valley's disc whichever points in the valley they stand at, so a winery's
// wines and the wines that only name its valley, made one disc further out,
// open the journal for the valley. Tastings with neither in common name
// nothing, and a click looks closer.
//
// The one exception is `closer`: two winery marks made one where the dots are
// drawn, which a closer zoom parts. They share a valley as often as not, and
// the mark still names nothing, because looking closer is what finds each
// winery's own mark.
function counted(site: Site, country: boolean): JournalMarkProperties {
  const [{ properties: first }] = site.features
  const shared = (of: 'place_slug' | 'winery_slug') =>
    new Set(site.features.map((feature) => feature.properties[of] || null)).size === 1
  const place = !site.closer && shared('place_slug') ? first : null
  const winery = first.winery_slug && shared('winery_slug') ? first : null

  return {
    point_count: site.count,
    point_count_abbreviated: abbreviated(site.count),
    place_slug: place?.place_slug ?? '',
    winery_slug: winery?.winery_slug ?? '',
    label: winery?.winery ?? place?.place ?? `Your ${tastings(site.count)}`,
    detail: country
      ? `${tastings(site.count)} · no appellation recorded`
      : winery
        ? winesFromHere(site.count)
        : place?.place
          ? `${tastings(site.count)} in your journal`
          : 'In several places — click to look closer',
    country,
  }
}

// What each map's tasting source was last handed, as text: a settle that would
// hand it the same marks hands it nothing. Weak, so a removed map takes its
// entry with it.
const shown = new WeakMap<Map, string>()

// And the same for each map's wishlist source (`placeWishes`).
const wished = new WeakMap<Map, string>()

const NO_MARKS: JournalMarks = { type: 'FeatureCollection', features: [] }

/**
 * Works the marks out for the map as it stands and hands them to the source,
 * if they are not the ones it has.
 *
 * Called when the tastings change, when the winery chips or the appellations
 * change, and when the camera comes to rest — once per settle, never per
 * frame. With no tastings, which is every visitor, it is one length check.
 * For a reader's journal it is the merge, the stepping clear and one
 * `JSON.stringify` of the result: timed in Node on a busy machine at 0.9 ms
 * for 112 tastings among 60 chips. The source is handed data only when the
 * marks have changed, so a pan that moves nothing re-tiles nothing.
 *
 * `wineryZoom` is where the winery layer starts drawing dots, when the caller
 * knows the server's own number for it (`journalMarks`).
 */
export function placeJournalMarks(
  map: Map,
  layer: JournalLayer,
  chips: readonly Obstacle[],
  anchor: NameAnchor = nowhere,
  wineryZoom?: number,
) {
  const source = map.getSource(TASTING_SOURCE_ID) as GeoJSONSource | undefined
  if (!source) return

  const marks =
    layer.features.length === 0 ? NO_MARKS : journalMarks(layer, map.getZoom(), chips, anchor, wineryZoom)
  const text = marks === NO_MARKS ? '' : JSON.stringify(marks.features)
  if (shown.get(map) === text) return

  shown.set(map, text)
  source.setData(marks)
}

/**
 * Hands the wishes to their source, if they are not the ones it has.
 *
 * A wishlist that arrives as a new object is not always a new wishlist. A
 * rating saved from a winery's panel replaces one prop, and Inertia does that
 * by copying every prop the page has, so each wine he rates hands the map a
 * copy of the same wishes. They are compared as text, like the marks above: a
 * reader's open wishes are a few dozen points at most.
 */
function placeWishes(map: Map, wishlist: JournalLayer) {
  const source = map.getSource(WISHLIST_SOURCE_ID) as GeoJSONSource | undefined
  if (!source) return

  const text = JSON.stringify(wishlist.features)
  if (wished.get(map) === text) return

  wished.set(map, text)
  source.setData(wishlist)
}

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

const isCounted = ['has', 'point_count']
const isWineryMark = ['has', 'wine_count']
// One tasting's own pin is whatever is neither of the other two.
const isPin = ['all', ['!', isCounted], ['!', isWineryMark]]
const countryOnly = ['==', ['get', 'country'], true]

export function addJournalLayers(
  map: Map,
  tastingLayer: JournalLayer,
  wishlist: JournalLayer,
  chips: readonly Obstacle[] = [],
  anchor: NameAnchor = nowhere,
  wineryZoom?: number,
) {
  // A style swap discards the previous style's sources, so this runs again
  // after a basemap failure — but addSource throws on a duplicate id. Same
  // guard region_layer.ts carries, for the same reason.
  if (map.getSource(TASTING_SOURCE_ID)) {
    setJournalData(map, tastingLayer, wishlist, chips, anchor, wineryZoom)
    return
  }

  // Added empty and filled below, through the one function that fills it ever
  // after — so what the source holds is always what `shown` says it holds.
  map.addSource(TASTING_SOURCE_ID, { type: 'geojson', data: NO_MARKS })
  map.addSource(WISHLIST_SOURCE_ID, { type: 'geojson', data: wishlist })
  shown.set(map, '')
  wished.set(map, JSON.stringify(wishlist.features))

  // Several tastings: a claret disc with their number in paper. Known only by
  // their country: the same disc inside out.
  map.addLayer({
    id: TASTING_CLUSTER_LAYER_ID,
    type: 'circle',
    source: TASTING_SOURCE_ID,
    filter: isCounted as never,
    paint: {
      'circle-color': ['case', countryOnly, TASTING_PIN_RING_COLOR, TASTING_PIN_COLOR] as never,
      // By the square root of the count, from the table the arithmetic above
      // reads: the disc that is drawn is the disc that was kept clear.
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['sqrt', ['get', 'point_count']],
        ...JOURNAL_RADIUS_STOPS.flat(),
      ] as never,
      'circle-stroke-width': ['case', countryOnly, 1.5, 2] as never,
      'circle-stroke-color': ['case', countryOnly, TASTING_PIN_COLOR, TASTING_PIN_RING_COLOR] as never,
    },
  })

  map.addLayer({
    id: TASTING_CLUSTER_COUNT_LAYER_ID,
    type: 'symbol',
    source: TASTING_SOURCE_ID,
    filter: isCounted as never,
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      // The same face and the same treatment as the winery chips' counts, so a
      // number inside a circle is set one way on this map: the roman, and no
      // halo to thicken it. It has to be a stack the glyph endpoint serves
      // (`SERVED_FONTSTACKS`), which is what a numeral outside the font file
      // would fall back to.
      'text-font': MARK_COUNT_FONT,
      'text-size': 11,
      // A count is always drawn: it is the mark, not a label on one.
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: { 'text-color': ['case', countryOnly, TASTING_PIN_COLOR, TASTING_PIN_RING_COLOR] as never },
  })

  // One tasting: a ringed disc, in two layers because a circle has one stroke.
  // The outer one is the claret ring and the whole of what a pointer or a
  // click is tested against; the core is the paper ring and the claret centre.
  map.addLayer({
    id: TASTING_POINT_LAYER_ID,
    type: 'circle',
    source: TASTING_SOURCE_ID,
    filter: isPin as never,
    paint: { 'circle-color': TASTING_PIN_COLOR, 'circle-radius': TASTING_PIN_RADII.ring },
  })

  map.addLayer({
    id: TASTING_POINT_CORE_LAYER_ID,
    type: 'circle',
    source: TASTING_SOURCE_ID,
    filter: isPin as never,
    paint: {
      'circle-color': TASTING_PIN_COLOR,
      'circle-radius': TASTING_PIN_RADII.core,
      'circle-stroke-width': TASTING_PIN_RADII.paper - TASTING_PIN_RADII.core,
      'circle-stroke-color': TASTING_PIN_RING_COLOR,
    },
  })

  // The winery mark (map_config.ts, `WINERY_MARK_MIN_ZOOM`): a claret dot on
  // the winery's own dot, small enough to sit inside a selected winery's amber
  // ring, and a badge up and to the right of it with how many wines.
  map.addLayer({
    id: TASTING_WINERY_LAYER_ID,
    type: 'circle',
    source: TASTING_SOURCE_ID,
    filter: isWineryMark as never,
    paint: {
      'circle-color': TASTING_PIN_COLOR,
      'circle-radius': WINERY_MARK_DOT_RADIUS,
      'circle-stroke-width': WINERY_MARK_RING_WIDTH,
      'circle-stroke-color': TASTING_PIN_RING_COLOR,
    },
  })

  // Translated in the paint rather than moved in the data: the mark's point is
  // the winery's, and the badge is a part of the drawing of it.
  map.addLayer({
    id: TASTING_WINERY_BADGE_LAYER_ID,
    type: 'circle',
    source: TASTING_SOURCE_ID,
    filter: isWineryMark as never,
    paint: {
      'circle-color': TASTING_PIN_COLOR,
      // Wider from three figures, which do not fit the smaller disc.
      'circle-radius': [
        'case',
        ['>=', ['get', 'wine_count'], WINERY_MARK_BADGE_WIDE_FROM],
        WINERY_MARK_BADGE_WIDE_RADIUS,
        WINERY_MARK_BADGE_RADIUS,
      ] as never,
      'circle-stroke-width': WINERY_MARK_RING_WIDTH,
      'circle-stroke-color': TASTING_PIN_RING_COLOR,
      'circle-translate': WINERY_MARK_BADGE_OFFSET,
    },
  })

  map.addLayer({
    id: TASTING_WINERY_COUNT_LAYER_ID,
    type: 'symbol',
    source: TASTING_SOURCE_ID,
    filter: isWineryMark as never,
    layout: {
      'text-field': ['get', 'wine_count_abbreviated'],
      // The counted discs' face, a size down for the smaller disc.
      'text-font': MARK_COUNT_FONT,
      'text-size': 10,
      // Drawn always, like every count: it is the mark, not a label on one.
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: { 'text-color': TASTING_PIN_RING_COLOR, 'text-translate': WINERY_MARK_BADGE_OFFSET },
  })

  // Unfilled, so the two layers are told apart by shape rather than by a colour
  // the atlas has already spent. "Unfilled" is a pale translucent wash rather
  // than nothing at all: a bare ring over a tinted country disappeared into it,
  // and over relief it is a ring round a hillside.
  map.addLayer({
    id: WISHLIST_POINT_LAYER_ID,
    type: 'circle',
    source: WISHLIST_SOURCE_ID,
    paint: {
      'circle-color': WISHLIST_PIN_FILL_COLOR,
      'circle-radius': 7,
      'circle-stroke-width': 2,
      'circle-stroke-color': TASTING_PIN_COLOR,
    },
  })

  placeJournalMarks(map, tastingLayer, chips, anchor, wineryZoom)
}

export function setJournalData(
  map: Map,
  tastingLayer: JournalLayer,
  wishlist: JournalLayer,
  chips: readonly Obstacle[] = [],
  anchor: NameAnchor = nowhere,
  wineryZoom?: number,
) {
  placeJournalMarks(map, tastingLayer, chips, anchor, wineryZoom)
  placeWishes(map, wishlist)
}

export function setJournalVisibility(map: Map, tastingsVisible: boolean, wishlistVisible: boolean) {
  for (const id of tastingLayerIds()) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', tastingsVisible ? 'visible' : 'none')
  }

  if (map.getLayer(WISHLIST_POINT_LAYER_ID)) {
    map.setLayoutProperty(WISHLIST_POINT_LAYER_ID, 'visibility', wishlistVisible ? 'visible' : 'none')
  }
}
